import {
  compassName,
  customLightId,
  ENVIRONMENT_TEMPLATES,
  GROUND_PARTS,
  LIGHT_PARTS,
  mixEnvironmentId,
  parseCustomLightId,
  partsOfEnvironment,
  PHYSICS_PARTS,
  SKY_PARTS,
  templateOfParts,
  type Environment,
  type EnvironmentParts,
} from '@rcs/data-schema';
import { useId } from 'react';
import { messages } from '../i18n/ja';

const CUSTOM = 'custom';
const DEFAULT_CUSTOM_LIGHT = { azimuth: 135, elevation: 45, intensity: 100 };

/** 太陽の設定のスライダー 1 本（値は数字でも示す）。 */
function SunSlider({
  label,
  unit,
  value,
  max,
  step,
  testId,
  onChange,
}: {
  readonly label: string;
  readonly unit: string;
  readonly value: number;
  readonly max: number;
  readonly step: number;
  readonly testId: string;
  readonly onChange: (value: number) => void;
}) {
  return (
    <label className="environment-sun__row">
      <span className="number-field__label">{label}</span>
      <input
        type="range"
        min={0}
        max={max}
        step={step}
        value={value}
        data-testid={testId}
        onChange={(event) => {
          onChange(Number(event.target.value));
        }}
      />
      <output data-testid={`${testId}-value`}>{`${String(value)}${unit}`}</output>
    </label>
  );
}

/** 部品の選択欄 1 つ。 */
function PartSelect({
  label,
  value,
  parts,
  testId,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly parts: readonly { readonly id: string; readonly name: string }[];
  readonly testId: string;
  readonly onChange: (id: string) => void;
}) {
  const id = useId();
  return (
    <div className="number-field">
      <label htmlFor={id} className="number-field__label">
        {label}
      </label>
      <select
        id={id}
        value={value}
        data-testid={testId}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        {parts.map((part) => (
          <option key={part.id} value={part.id}>
            {part.name}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * 背景（環境）の選択（FR-ENV）。おすすめの組み合わせから選ぶか、空・地面・光・物理の部品を
 * 組み合わせて作る。物理（重力・空気密度）も背景の部品で、走行の計算に使われる。
 * 選んだ背景の要点を、文言でも示す。
 */
export function EnvironmentPanel({
  environment,
  onSelect,
}: {
  readonly environment: Environment | undefined;
  readonly onSelect: (id: string) => void;
}) {
  const t = messages.environment;
  const templateId = useId();
  const parts = environment ? partsOfEnvironment(environment.id) : undefined;
  const customLight = parts ? parseCustomLightId(parts.light) : undefined;
  const template = parts ? templateOfParts(parts) : undefined;
  const change = (patch: Partial<EnvironmentParts>): void => {
    if (parts) onSelect(mixEnvironmentId({ ...parts, ...patch }));
  };
  return (
    <section className="vehicle-panel" aria-label={t.title} data-testid="environment-panel">
      <div className="number-field">
        <label htmlFor={templateId} className="number-field__label">
          {t.template}
        </label>
        <select
          id={templateId}
          value={template?.id ?? ''}
          data-testid="environment-template"
          onChange={(event) => {
            const chosen = ENVIRONMENT_TEMPLATES.find((item) => item.id === event.target.value);
            if (chosen) onSelect(mixEnvironmentId(chosen.parts));
          }}
        >
          {template === undefined && <option value="">{t.custom}</option>}
          {ENVIRONMENT_TEMPLATES.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </div>
      {parts && (
        <>
          <PartSelect
            label={t.sky}
            value={parts.sky}
            parts={SKY_PARTS}
            testId="environment-sky"
            onChange={(sky) => {
              change({ sky });
            }}
          />
          <PartSelect
            label={t.ground}
            value={parts.ground}
            parts={GROUND_PARTS}
            testId="environment-ground"
            onChange={(ground) => {
              change({ ground });
            }}
          />
          <PartSelect
            label={t.light}
            value={customLight ? CUSTOM : parts.light}
            parts={[...LIGHT_PARTS, { id: CUSTOM, name: t.customLight }]}
            testId="environment-light"
            onChange={(light) => {
              change({ light: light === CUSTOM ? customLightId(DEFAULT_CUSTOM_LIGHT) : light });
            }}
          />
          {customLight && (
            <div className="environment-sun" data-testid="environment-sun">
              <SunSlider
                label={t.azimuth}
                unit="°"
                value={customLight.azimuth}
                max={359}
                step={5}
                testId="environment-sun-azimuth"
                onChange={(azimuth) => {
                  change({ light: customLightId({ ...customLight, azimuth }) });
                }}
              />
              <SunSlider
                label={t.elevation}
                unit="°"
                value={customLight.elevation}
                max={90}
                step={5}
                testId="environment-sun-elevation"
                onChange={(elevation) => {
                  change({ light: customLightId({ ...customLight, elevation }) });
                }}
              />
              <SunSlider
                label={t.intensity}
                unit="%"
                value={customLight.intensity}
                max={300}
                step={10}
                testId="environment-sun-intensity"
                onChange={(intensity) => {
                  change({ light: customLightId({ ...customLight, intensity }) });
                }}
              />
              <p className="vehicle-panel__facts" data-testid="environment-sun-facts">
                {t.sunFacts
                  .replace('{compass}', compassName(customLight.azimuth))
                  .replace('{azimuth}', String(customLight.azimuth))
                  .replace('{elevation}', String(customLight.elevation))}
              </p>
            </div>
          )}
          <PartSelect
            label={t.physics}
            value={parts.physics}
            parts={PHYSICS_PARTS}
            testId="environment-physics"
            onChange={(physics) => {
              change({ physics });
            }}
          />
        </>
      )}
      {environment && (
        <p className="vehicle-panel__facts" data-testid="environment-facts">
          {t.facts
            .replace('{gravity}', environment.physics.gravity.toFixed(2))
            .replace('{air}', environment.physics.airDensity.toFixed(3))}
        </p>
      )}
    </section>
  );
}
